import { Operation } from '@/types/general';
import { ManageForm } from '@components/forms/ManageForm';
import { useResellerMutation, useNameMapQuery, useResellerQuery } from '@features/redux/apis/org';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    MenuItem,
    Select,
    TextField,
    Paper,
    Typography,
} from '@mui/material';
import { getIdDisplayValue } from '@utils/index';
import { GridSearchSelect, type SearchSelectOption } from '@components/forms/GridSearchSelect';
import type { RootState } from '@features/redux/store';
import { FormRequiredText } from '@components/forms/text/base/FormRequiredText';
import { Validate } from '@utils/validate';
import { ResellerEffects } from './ResellerEffects';
import { Query } from '@features/redux/apis/utils';
import { BILLING_OPTIONS, DEFAULT_BILLING_OPTIONS, WARRANTY_OPTIONS } from '@consts/company';
import { FormDisabledText } from '@components/forms/text/base/FormDisabledText';
import { FormSelect } from '@components/forms/dropdowns/base/FormSelect';
import { useStyles } from '@components/styles/NewOrUpdateForm.styles';

import type { ManageProps } from '../Panel';
import { useEffect, useState } from 'react';
import { AuditTimeline } from '@components/AuditTrail/AuditTimeline';

// ? Don't use withDraft unless we need to. We don't need to
// ? create a draft in the ResellerEffects component, for example.
const useResellers = Query.withDraft(useResellerQuery, {
    ResellerCompanyID: 0,
    CompanyName: '',
    Inactive: false,
    QBName: undefined,
    SalesRepTrackingEnabled: null,
    ...DEFAULT_BILLING_OPTIONS,
});

export const Reseller = ({ id: resellerId, operation, onSuccess }: ManageProps) => {
    // const navigate = useNavigate();

    const { role } = useSelector(({ auth }: RootState) => auth.Roles);
    const auditPk = resellerId ? `Reseller#${resellerId}` : null;

    const { data: nameMap } = useNameMapQuery({});

    const {
        draft,
        data,
        isFetching: queryLoading,
    } = useResellers({ resellerId: +resellerId! }, { skip: operation === Operation.Create });

    const [mutate, { isLoading: mutationLoading }] = useResellerMutation({
        // We don't want to use the mutation cache if we're creating a new reseller
        fixedCacheKey: operation === Operation.Edit ? 'ResellerMutation' : 'ResellerCreation',
    });

    const isLoading = queryLoading || mutationLoading;

    const [draftState, setDraftState] = draft;

    /**
     * Following pattern from Location page for Consent to sell (cts)
     */
    const [salesRepTracking, setSalesRepTracking] = useState<boolean | 'inherit'>('inherit');
    useEffect(
        () =>
            void setSalesRepTracking(
                data?.SalesRepTrackingEnabled === null ? 'inherit' : Boolean(data?.SalesRepTrackingEnabled)
            ),
        [data?.SalesRepTrackingEnabled]
    );

    const isAdmin = role.isAdmin && role.stores.edit;

    const classes = useStyles();

    return (
        <>
            <ResellerEffects />
            <ManageForm
                title={operation === Operation.Create ? 'Create New Reseller' : 'Manage Reseller'}
                loading={isLoading}
                size={12}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Paper elevation={0} sx={{ width: '100%', padding: 1, border: '1px solid #e0e0e0' }}>
                        <Typography fontSize={'18px'} pt={1} pl={1} pb={1} fontWeight={1000} color={'black'}>
                            Reseller Info
                        </Typography>

                        <Grid container spacing={2} p={1}>
                            {operation === Operation.Edit && role.isAdmin && (
                                <>
                                    <FormDisabledText
                                        size={12}
                                        label='Reseller ID'
                                        value={data?.CompanyID?.toString() || 'N/A'}
                                    />
                                    <FormDisabledText
                                        size={12}
                                        label='Linked Company ID'
                                        value={getIdDisplayValue(data?.ResellerCompanyID)}
                                    />
                                </>
                            )}

                            <GridSearchSelect
                                state={draftState}
                                dispatch={setDraftState}
                                disabled={operation === Operation.View || operation === Operation.Edit}
                                getLabel={(idKey) => nameMap?.companies[idKey] ?? ''}
                                idKey={'ResellerCompanyID'}
                                size={12}
                                label='Linked Company'
                                options={
                                    Object.entries(nameMap?.companies ?? {}).map(([key, value]) => ({
                                        id: key,
                                        name: value,
                                    })) as SearchSelectOption[]
                                }
                            />

                            <FormRequiredText
                                size={12}
                                disabled={operation === Operation.View}
                                stateKey='CompanyName'
                                label='Reseller Name'
                                draft={draft}
                                validateFn={Validate.name}
                                helperText={{
                                    valid: '',
                                    invalid: 'Please provide a valid reseller name',
                                }}
                            />

                            {role.stores.edit && (
                                <Grid item xs={12} sm={12} md={12}>
                                    <Typography variant='body2' color='text.primary' fontWeight={1000} sx={{ pl: 0.5 }}>
                                        Sales Rep Tracking
                                    </Typography>

                                    <FormControl fullWidth size='small' disabled={operation === Operation.View}>
                                        <Select
                                            value={salesRepTracking}
                                            onChange={({ target }) => {
                                                const val = target.value as boolean | 'inherit';
                                                setSalesRepTracking(val);
                                                setDraftState((prev) => ({
                                                    ...prev,
                                                    SalesRepTrackingEnabled:
                                                        val === 'inherit' ? null : (val as boolean),
                                                }));
                                            }}>
                                            <MenuItem value='inherit'>Inherit from Company</MenuItem>
                                            <MenuItem value={true as any}>Enabled</MenuItem>
                                            <MenuItem value={false as any}>Disabled</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {role.stores.edit && (
                                <Grid item xs={12} sm={12} md={12} display='flex' gap='10px'>
                                    <Button
                                        type='submit'
                                        disabled={!Validate.Org.reseller(draftState)}
                                        size={'large'}
                                        variant={'contained'}
                                        color={'orange'}
                                        onClick={async () => {
                                            await mutate({
                                                method: operation === Operation.Create ? 'POST' : 'PATCH',
                                                reseller: draftState,
                                            });

                                            onSuccess?.();
                                        }}>
                                        {operation === Operation.Create ? 'Create' : 'Update'}
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    {auditPk && operation !== Operation.Create && role.isAdmin && (
                        <Paper elevation={0} sx={{ width: '100%', padding: 1, border: '1px solid #e0e0e0', mt: 2 }}>
                            <Typography fontSize={'18px'} pt={1} pl={1} pb={1} fontWeight={1000} color={'black'}>
                                Audit Log
                            </Typography>
                            <AuditTimeline entityPk={auditPk} />
                        </Paper>
                    )}
                </Grid>
            </ManageForm>
        </>
    );
};
